/* Vista Services — itinerary cards with route numbers, durations,
   and "departure" CTAs. Reads like a travel-booking ledger. */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { tenantHref } from "@/lib/tenant";
import { EditorialSkeleton } from "../editorial/_EditorialSkeleton";

function priceFrom(svc) {
  const tiers = Array.isArray(svc?.tiers) ? svc.tiers : [];
  const tp = tiers.map(t => Number(t?.price)).filter(n => Number.isFinite(n) && n > 0);
  if (tp.length) return Math.min(...tp);
  const n = Number(svc?.price_from ?? svc?.starting_price ?? svc?.price);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export default function VistaServices() {
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const [services, setServices] = useState(null);
  useEffect(() => {
    api.get("/services").then(r => setServices(Array.isArray(r.data) ? r.data : [])).catch(() => setServices([]));
    document.title = `${studioName} — Expeditions`;
  }, [studioName]);

  if (services === null) return <EditorialSkeleton eyebrow="Plotting routes." />;

  return (
    <div data-testid="vista-services" style={{ background: "var(--tenant-bg, #F8F9FB)", color: "var(--tenant-fg, #102A33)" }}>

      <section className="px-6 lg:px-12 pt-16 pb-10 border-b" style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 30%, transparent)" }}>
        <div className="max-w-[1480px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-3">
            <div className="text-[9px] uppercase tracking-[0.42em] mb-3" style={{ color: "var(--tenant-accent)" }}>
              Departures · {new Date().getFullYear()}
            </div>
          </div>
          <div className="md:col-span-9">
            <h1 className="leading-[0.95]" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(2.5rem, 6vw, 4.5rem)" }}>
              Routes we run.
            </h1>
          </div>
        </div>
      </section>

      <div className="max-w-[1480px] mx-auto px-6 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-2 gap-6">
        {services.map((svc, i) => {
          const p = priceFrom(svc);
          return (
            <article key={svc.id} id={svc.id || svc.slug}
              className="border-2 p-7 transition-colors hover:bg-[color:var(--tenant-accent)]/5"
              style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 30%, transparent)" }}>
              <div className="flex items-center justify-between text-[9px] uppercase tracking-[0.42em] mb-6">
                <span style={{ color: "var(--tenant-accent)" }}>Route №{String(i + 1).padStart(2, "0")}</span>
                <span style={{ color: "var(--tenant-fg-soft)" }}>
                  {svc.duration_minutes ? `${Math.round(svc.duration_minutes / 60)} HOURS` : "FULL DAY"}
                </span>
              </div>
              <h2 className="text-3xl leading-tight mb-3" style={{ fontFamily: "var(--tenant-serif)" }}>{svc.name}</h2>
              {(svc.description || svc.short_description) && (
                <p className="text-base leading-relaxed mb-6" style={{ color: "var(--tenant-fg-soft)" }}>{svc.description || svc.short_description}</p>
              )}
              <div className="flex items-end justify-between border-t pt-5"
                   style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 30%, transparent)" }}>
                {p !== null && (
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-fg-soft)" }}>Fare from</div>
                    <div className="text-2xl tabular-nums" style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-accent)" }}>
                      ${p.toFixed(0)}
                    </div>
                  </div>
                )}
                <Link to={tenantHref("/booking")}
                  className="text-[10px] uppercase tracking-[0.32em] border-2 px-5 py-3"
                  style={{ borderColor: "var(--tenant-accent)", color: "var(--tenant-accent)" }}>
                  Depart →
                </Link>
              </div>
            </article>
          );
        })}
        {services.length === 0 && (
          <p className="col-span-2 text-center text-sm uppercase tracking-[0.42em] py-12" style={{ color: "var(--tenant-fg-soft)" }}>
            No departures scheduled.
          </p>
        )}
      </div>
    </div>
  );
}
